import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NavigationEnd, Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter} from 'rxjs/operators';

/**
 * Garde un Set des clés de traduction rencontrées sur la page courante.
 * Patche TranslateService.get / instant / stream pour intercepter chaque appel.
 * Reset automatiquement à chaque NavigationEnd.
 *
 * Activé uniquement pour les admins (start() appelé depuis AppComponent).
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationTrackerService {

  private started = false;
  private keys = new Set<string>();
  private readonly _keys$ = new BehaviorSubject<string[]>([]);

  constructor(
    private translate: TranslateService,
    private router: Router
  ) {
  }

  get keys$(): Observable<string[]> {
    return this._keys$.asObservable();
  }

  /**
   * Patche TranslateService pour capturer les clés utilisées,
   * et s'abonne au router pour reset entre les pages.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.patchTranslateMethod('instant');
    this.patchTranslateMethod('get');
    this.patchTranslateMethod('stream');

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.reset());
  }

  reset(): void {
    this.keys.clear();
    this._keys$.next([]);
  }

  private track(key: string | string[]): void {
    if (Array.isArray(key)) {
      let changed = false;
      for (const k of key) {
        if (typeof k === 'string' && !this.keys.has(k)) {
          this.keys.add(k);
          changed = true;
        }
      }
      if (changed) {
        this.emit();
      }
    } else if (typeof key === 'string' && !this.keys.has(key)) {
      this.keys.add(key);
      this.emit();
    }
  }

  private emit(): void {
    this._keys$.next(Array.from(this.keys).sort((a, b) => a.localeCompare(b)));
  }

  private patchTranslateMethod(method: 'instant' | 'get' | 'stream'): void {
    const original = (this.translate as any)[method].bind(this.translate);
    const tracker = this;
    (this.translate as any)[method] = function (key: string | string[], ...args: any[]) {
      tracker.track(key);
      return original(key, ...args);
    };
  }
}
